import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  fetchQuestion,
  fetchQuestions,
  hideQuestionApi,
  hideResponseApi,
  respondToQuestionApi,
  takeQuestionApi,
  unhideQuestionApi,
  unhideResponseApi,
} from "../services/questionsService";
import { resolveApiErrorMessage } from "../utils/resolveApiErrorMessage";

const QUESTIONS_QK = ["questions"];
const FIVE_MINUTES = 5 * 60 * 1000;

const normalizeList = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return data?.results || [];
};

export const useQuestions = (params = {}, options = {}) =>
  useQuery({
    queryKey: [...QUESTIONS_QK, params],
    queryFn: () => fetchQuestions(params),
    staleTime: FIVE_MINUTES,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    select: normalizeList,
    ...options,
  });

export const useQuestion = (id, options = {}) =>
  useQuery({
    queryKey: [...QUESTIONS_QK, id],
    queryFn: () => fetchQuestion(id),
    enabled: Boolean(id),
    staleTime: FIVE_MINUTES,
    refetchInterval: FIVE_MINUTES,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    ...options,
  });

export const useRespondToQuestion = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }) => respondToQuestionApi(id, payload),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: QUESTIONS_QK });
      if (variables?.id) {
        qc.invalidateQueries({ queryKey: [...QUESTIONS_QK, variables.id] });
      }
      toast.success("Réponse envoyée", { autoClose: 3000 });
    },
    onError: (err) => {
      const msg = resolveApiErrorMessage(err, "Réponse non envoyée");
      toast.error(msg, { autoClose: 3000 });
    },
  });
};

export const useTakeQuestion = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }) => takeQuestionApi(id, payload),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: QUESTIONS_QK });
      if (variables?.id) {
        qc.invalidateQueries({ queryKey: [...QUESTIONS_QK, variables.id] });
      }
    },
    onError: (err) => {
      const msg = resolveApiErrorMessage(err, "Question non prise");
      toast.error(msg, { autoClose: 3000 });
    },
  });
};

/**
 * Moderation is after the fact: a question is public as soon as it is asked,
 * and hiding it is how it comes down. Reversible, so a wrong call costs
 * nothing.
 */
const moderation = (mutationFn, successMessage, errorMessage) => () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: QUESTIONS_QK });
      if (id) {
        qc.invalidateQueries({ queryKey: [...QUESTIONS_QK, id] });
      }
      toast.success(successMessage, { autoClose: 3000 });
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err, errorMessage), {
        autoClose: 3000,
      });
    },
  });
};

export const useHideQuestion = moderation(
  (id) => hideQuestionApi(id),
  "Question masquée",
  "Question non masquée"
);

export const useUnhideQuestion = moderation(
  (id) => unhideQuestionApi(id),
  "Question réaffichée",
  "Question non réaffichée"
);

export const useHideResponse = moderation(
  (id) => hideResponseApi(id),
  "Réponse masquée",
  "Réponse non masquée"
);

export const useUnhideResponse = moderation(
  (id) => unhideResponseApi(id),
  "Réponse réaffichée",
  "Réponse non réaffichée"
);
